import { useState } from 'react'
import { IconChevronLeft, IconChevronRight } from '@tabler/icons-react'
import { Typography } from '../../common/typography'
import classes from './PeriodPicker.module.css'
import { quarterOf, quarterStart } from '../../widgets/quarter'

const quarters = ['Q1', 'Q2', 'Q3', 'Q4']

const months = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
]

interface PeriodPickerProps {
  start_month: string
  end_month: string
  periodType?: 'total' | 'month' | 'quarter' | 'year'
  period?: string
  onChange: (periodType: string, period: string) => void
}

// The `total` period type always uses this fixed sentinel date on the backend
// (see api/src/metrics/queries/generate_context.sql) — it is not a real period.
const TOTAL_PERIOD = '2025-01-01'

export default function PeriodPicker({
  start_month,
  end_month,
  periodType: controlledPeriodType = 'total',
  period: controlledPeriod = TOTAL_PERIOD,
  onChange,
}: PeriodPickerProps) {
  // Parse controlled period to extract month/year info
  const parsePeriod = (period: string) => {
    if (!period) return { monthIndex: null, year: null }
    const [year, month] = period.split('-')
    return {
      monthIndex: month ? parseInt(month, 10) - 1 : null,
      year: year ? parseInt(year, 10) : null,
    }
  }

  const { monthIndex, year } = parsePeriod(controlledPeriod)

  const startDate = new Date(start_month)
  const endDate = new Date(end_month)
  const startYear = startDate.getFullYear()
  const startMonthIndex = startDate.getMonth()
  const endYear = endDate.getFullYear()
  const endMonthIndex = endDate.getMonth()

  // The year to show when opening month/quarter. Only trust the controlled
  // period's year when a real month/quarter is selected — in `total`/`year`
  // mode the controlled period is a sentinel (e.g. 2025-01-01) whose year must
  // NOT leak into the view. Fall back to the competition's start year.
  const selectedRealYear =
    (controlledPeriodType === 'month' || controlledPeriodType === 'quarter') &&
    year
      ? year
      : null
  const defaultViewYear = selectedRealYear ?? startYear

  // Local state for which period type is being selected (UI state)
  const [localPeriodType, setLocalPeriodType] = useState<
    'total' | 'month' | 'quarter' | 'year'
  >(controlledPeriodType)

  const [viewYear, setViewYear] = useState<number>(defaultViewYear)

  // Generate years array dynamically from start to end year
  const years = Array.from({ length: endYear - startYear + 1 }, (_, i) =>
    (startYear + i).toString()
  )

  const handlePeriodTypeChange = (
    type: 'total' | 'month' | 'quarter' | 'year'
  ) => {
    setLocalPeriodType(type)

    if (type === 'total') {
      // Total always uses the fixed sentinel period, regardless of competition.
      onChange?.('total', TOTAL_PERIOD)
    } else if (type === 'month' || type === 'quarter') {
      // Just switch to the month/quarter view, wait for the user to pick.
      // Land on the competition's start year unless a real month/quarter is
      // already selected (avoids the `total` sentinel year leaking in).
      setViewYear(defaultViewYear)
    } else if (type === 'year') {
      // Just switch to year view, wait for user to pick a year
    }
  }

  const handleMonthClick = (index: number) => {
    if (isMonthDisabled(index)) return

    const period = formatMonthDate(viewYear, index)
    onChange?.('month', period)
  }

  const handleQuarterClick = (quarter: number) => {
    if (isQuarterDisabled(quarter)) return
    onChange?.('quarter', quarterStart(viewYear, quarter))
  }

  const isQuarterSelected = (quarter: number) => {
    return (
      localPeriodType === 'quarter' &&
      controlledPeriodType === 'quarter' &&
      year === viewYear &&
      quarterOf(controlledPeriod) === quarter
    )
  }

  const isQuarterDisabled = (quarter: number) => {
    const quarterStartDate = new Date(viewYear, (quarter - 1) * 3)
    const quarterEndDate = new Date(viewYear, (quarter - 1) * 3 + 2)
    const startDateObj = new Date(startYear, startMonthIndex)
    const endDateObj = new Date(endYear, endMonthIndex)

    return quarterEndDate < startDateObj || quarterStartDate > endDateObj
  }

  const handlePrevYear = () => {
    if (viewYear > startYear) {
      setViewYear((prev) => prev - 1)
    }
  }

  const handleNextYear = () => {
    if (viewYear < endYear) {
      setViewYear((prev) => prev + 1)
    }
  }

  const handleYearClick = (clickedYear: number) => {
    const period = `${clickedYear}-01-01`
    onChange?.('year', period)
  }

  const formatMonthDate = (year: number, monthIndex: number) => {
    const month = String(monthIndex + 1).padStart(2, '0')
    return `${year}-${month}-01`
  }

  const isMonthSelected = (index: number) => {
    // Only show selection if controlled type is month and matches the current view
    return (
      localPeriodType === 'month' &&
      controlledPeriodType === 'month' &&
      monthIndex === index &&
      year === viewYear
    )
  }

  const isMonthDisabled = (index: number) => {
    const currentMonthDate = new Date(viewYear, index)
    const startDateObj = new Date(startYear, startMonthIndex)
    const endDateObj = new Date(endYear, endMonthIndex)

    return currentMonthDate < startDateObj || currentMonthDate > endDateObj
  }

  return (
    <div className={classes.periodPickerWrapper}>
      <div className={classes.periodTypeContainer}>
        <div
          className={classes.periodType}
          onClick={() => handlePeriodTypeChange('total')}
          data-selected={localPeriodType === 'total'}
        >
          <Typography
            fs="sm"
            fc={{
              light: localPeriodType === 'total' ? 'white' : 'gray-9',
              dark: localPeriodType === 'total' ? 'white' : 'gray-3',
            }}
            lh="140"
          >
            Total
          </Typography>
        </div>
        <div
          className={classes.periodType}
          onClick={() => handlePeriodTypeChange('month')}
          data-selected={localPeriodType === 'month'}
        >
          <Typography
            fs="sm"
            fc={{
              light: localPeriodType === 'month' ? 'white' : 'gray-9',
              dark: localPeriodType === 'month' ? 'white' : 'gray-3',
            }}
            lh="140"
          >
            Month
          </Typography>
        </div>
        <div
          className={classes.periodType}
          onClick={() => handlePeriodTypeChange('quarter')}
          data-selected={localPeriodType === 'quarter'}
        >
          <Typography
            fs="sm"
            fc={{
              light: localPeriodType === 'quarter' ? 'white' : 'gray-9',
              dark: localPeriodType === 'quarter' ? 'white' : 'gray-3',
            }}
            lh="140"
          >
            Quarter
          </Typography>
        </div>
        <div
          className={classes.periodType}
          onClick={() => handlePeriodTypeChange('year')}
          data-selected={localPeriodType === 'year'}
        >
          <Typography
            fs="sm"
            fc={{
              light: localPeriodType === 'year' ? 'white' : 'gray-9',
              dark: localPeriodType === 'year' ? 'white' : 'gray-3',
            }}
            lh="140"
          >
            Year
          </Typography>
        </div>
      </div>

      {localPeriodType === 'month' && (
        <>
          <div className={classes.yearNavigation}>
            <IconChevronLeft
              className={classes.icon}
              onClick={handlePrevYear}
              data-disabled={viewYear <= startYear}
            />
            <Typography fs="md" lh="140" fw={500}>
              {viewYear}
            </Typography>
            <IconChevronRight
              className={classes.icon}
              onClick={handleNextYear}
              data-disabled={viewYear >= endYear}
            />
          </div>
          <div className={classes.itemsContainer}>
            {months.map((month, index) => {
              const disabled = isMonthDisabled(index)
              const selected = isMonthSelected(index)
              return (
                <div
                  key={month}
                  className={classes.item}
                  onClick={() => handleMonthClick(index)}
                  data-selected={selected}
                  data-disabled={disabled}
                >
                  <Typography
                    fs="sm"
                    fc={{
                      light: selected
                        ? 'white'
                        : disabled
                          ? 'gray-5'
                          : 'gray-9',
                      dark: selected ? 'white' : disabled ? 'gray-6' : 'gray-3',
                    }}
                    lh="140"
                    tt="uppercase"
                  >
                    {month}
                  </Typography>
                </div>
              )
            })}
          </div>
        </>
      )}
      {localPeriodType === 'quarter' && (
        <>
          <div className={classes.yearNavigation}>
            <IconChevronLeft
              className={classes.icon}
              onClick={handlePrevYear}
              data-disabled={viewYear <= startYear}
            />
            <Typography fs="md" lh="140" fw={500}>
              {viewYear}
            </Typography>
            <IconChevronRight
              className={classes.icon}
              onClick={handleNextYear}
              data-disabled={viewYear >= endYear}
            />
          </div>
          <div className={classes.itemsContainer}>
            {quarters.map((label, index) => {
              const quarter = index + 1
              const disabled = isQuarterDisabled(quarter)
              const selected = isQuarterSelected(quarter)
              return (
                <div
                  key={label}
                  className={classes.item}
                  onClick={() => handleQuarterClick(quarter)}
                  data-selected={selected}
                  data-disabled={disabled}
                >
                  <Typography
                    fs="sm"
                    fc={{
                      light: selected
                        ? 'white'
                        : disabled
                          ? 'gray-5'
                          : 'gray-9',
                      dark: selected ? 'white' : disabled ? 'gray-6' : 'gray-3',
                    }}
                    lh="140"
                    tt="uppercase"
                  >
                    {label}
                  </Typography>
                </div>
              )
            })}
          </div>
        </>
      )}
      {localPeriodType === 'year' && (
        <div className={classes.itemsContainer}>
          {years.map((yearStr) => {
            const yearNum = Number.parseInt(yearStr)
            const isSelected =
              localPeriodType === 'year' &&
              controlledPeriodType === 'year' &&
              year === yearNum
            return (
              <div
                key={yearStr}
                className={classes.item}
                onClick={() => handleYearClick(yearNum)}
                data-selected={isSelected}
              >
                <Typography
                  fs="sm"
                  fc={{
                    light: isSelected ? 'white' : 'gray-9',
                    dark: isSelected ? 'white' : 'gray-3',
                  }}
                  lh="140"
                  tt="uppercase"
                >
                  {yearStr}
                </Typography>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
